import {
    ISharingInformation, ISharingLinkSettings, Mode, ICustomLabeledAudienceConfig,
    ISharingItemInformation, ClientId, ShareType, IEngagementExtraData
} from '../../interfaces/SharingInterfaces';
import { autobind } from 'office-ui-fabric-react/lib/Utilities';
import { ModifyPermissions } from '../ModifyPermissions/ModifyPermissions';
import { ShareNotification } from '../ShareNotification/ShareNotification';
import { ShareViewState } from '../Share/Share';
import { IPerson } from '@ms/odsp-datasources/lib/PeoplePicker';
import stringsResx from '../Share.resx';
import { Spinner, SpinnerType } from 'office-ui-fabric-react/lib/Spinner';
import * as React from 'react';
import * as ShareCommunicationManager from '../../utilities/ShareCommunicationManager';
import * as PropTypes from 'prop-types';

export interface ICopyLinkProps {
    clientId: ClientId;
    scenarioId: string;
    companyName: string;
    currentSettings: ISharingLinkSettings;
    item: ISharingItemInformation;
    onLinkPermissionsApplyClicked: (currentSettings: ISharingLinkSettings) => void;
    onLinkPermissionsCancelClicked: () => void;
    onSelectedPeopleChange: (items: Array<any>) => void;
    onShareHintClicked: () => void;
    sharingInformation: ISharingInformation;
    showExistingAccessOption: boolean;
    viewState: ShareViewState;
    groupsMemberCount: number;
    onViewPolicyTipClicked: () => void;
    createPeopleSharingLinks?: boolean;
    displayUrl: string;
    mode: Mode;
    engagementExtraData: IEngagementExtraData;
    recipients: IPerson[];
    isLoadingDefaultRecipients: boolean;
    customLabeledAudienceConfig: ICustomLabeledAudienceConfig;
    preferCustomLabeledAudience: boolean;
    shareId: string;
}

export interface ICopyLinkState {
    shouldCopyLinkToClipboard: boolean;
}

export class CopyLink extends React.Component<ICopyLinkProps, ICopyLinkState> {
    private _initialLinkShown = false;
    private _shareClickTime: number;

    static contextTypes = {
        shareClickTime: PropTypes.number
    };

    constructor(props: ICopyLinkProps, context: any) {
        super(props);
        this._shareClickTime = context.shareClickTime;
        this.state = {
            shouldCopyLinkToClipboard: true
        };
    }

    render(): React.ReactElement<{}> {
        const props = this.props;
        const sharingInformation = props.sharingInformation;

        switch (this.props.viewState) {
            case ShareViewState.linkSuccess:
                const sharingHintProps = {
                    clientId: props.clientId,
                    companyName: props.companyName,
                    currentSettings: props.currentSettings,
                    sharingInformation: sharingInformation,
                    onShareHintClick: props.onShareHintClicked,
                    linkRecipients: null,
                    customLabeledAudienceConfig: props.customLabeledAudienceConfig
                };

                if (!this._initialLinkShown) {
                    // Attempt to notify host that UI is really ready.
                    ShareCommunicationManager.pageFinishedLoading(this._shareClickTime, this.props.clientId, Mode.copy, this.props.scenarioId);
                    this._initialLinkShown = true;
                }

                return (
                    <ShareNotification
                        shareType={ ShareType.copy }
                        itemName={ sharingInformation.item.name }
                        linkUrl={ props.displayUrl }
                        clientId={ props.clientId }
                        sharingHintProps={ sharingHintProps }
                        shouldCopyLinkToClipboard={ this.state.shouldCopyLinkToClipboard }
                        customLabeledAudienceConfig={ this.props.customLabeledAudienceConfig }
                    />
                );
            case ShareViewState.modifyPermissions:
                return (
                    <ModifyPermissions
                        clientId={ props.clientId }
                        shareId={ props.shareId }
                        mode={ props.mode }
                        companyName={ props.companyName }
                        currentSettings={ props.currentSettings }
                        doesCreate={ true }
                        onCancel={ this._onLinkSettingsCancelClick }
                        onSelectedPermissionsChange={ this._onLinkSettingsApplyClicked }
                        sharingInformation={ props.sharingInformation }
                        showExistingAccessOption={ true }
                        groupsMemberCount={ props.groupsMemberCount }
                        onViewPolicyTipClicked={ props.onViewPolicyTipClicked }
                        createPeopleSharingLinks={ props.createPeopleSharingLinks }
                        copyLinkShortcut={ true }
                        engagementExtraData={ this.props.engagementExtraData }
                        recipients={ this.props.recipients }
                        isLoadingDefaultRecipients={ this.props.isLoadingDefaultRecipients }
                        customLabeledAudienceConfig={ this.props.customLabeledAudienceConfig }
                        preferCustomLabeledAudience={ this.props.preferCustomLabeledAudience }
                        onSelectedPeopleChange={ () => { } }
                    />
                );
            default:
                return (
                    <div className='od-Share-spinnerHolder'>
                        <Spinner
                            className='od-Share-spinner'
                            label={ stringsResx.componentLoading }
                            type={ SpinnerType.large }
                        />
                    </div>
                );
        }
    }

    @autobind
    private _onLinkSettingsCancelClick() {
        this.setState({
            shouldCopyLinkToClipboard: false
        }, () => {
            this.props.onLinkPermissionsCancelClicked();
        });
    }

    @autobind
    private _onLinkSettingsApplyClicked(settings: ISharingLinkSettings) {
        this.setState({
            shouldCopyLinkToClipboard: true
        }, () => {
            this.props.onLinkPermissionsApplyClicked(settings);
        });
    }
}